
<template>
    <Teleport to="body">
      <Transition name="renew-fade">
        <div
          v-if="modelValue"
          class="renew-overlay"
          @click.self="closeDrawer"
        >
          <Transition name="renew-slide" appear>
            <div
              class="renew-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="renew-title"
            >
              <!-- Header -->
              <div class="renew-header">
                <div class="renew-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 3H18L22 9L12 21L2 9L6 3Z"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M2 9H22M9 3L7 9L12 21L17 9L15 3"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
  
                <div class="renew-heading">
                  <span class="renew-eyebrow">
                    HR3 PREMIUM
                  </span>
  
                  <h3 id="renew-title">
                    Подписка скоро закончится
                  </h3>
                </div>
  
                <button
                  type="button"
                  class="renew-close"
                  aria-label="Закрыть"
                  @click="closeDrawer"
                >
                  ×
                </button>
              </div>
  
              <!-- Description -->
              <p class="renew-description">
                Продлите подписку, чтобы сохранить
                доступ ко всем премиум-возможностям HR3.
              </p>
  
              <!-- Actions -->
              <div class="renew-actions">
                <button
                  type="button"
                  class="renew-later"
                  @click="closeDrawer"
                >
                  Позже
                </button>
  
                <button
                  type="button"
                  class="renew-primary"
                  @click="openPaywall"
                >
                  <span>Продлить подписку</span>
  
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12H19M13 6L19 12L13 18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
  
              <p class="renew-footnote">
                Вы можете вернуться к продлению позже
              </p>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </template>
  
  <script setup>
  import { watch, onBeforeUnmount } from "vue";
  
  // ============================================================
  // PROPS
  // ============================================================
  
  const props = defineProps({
    modelValue: {
      type: Boolean,
      default: false
    }
  });
  
  // ============================================================
  // EMITS
  // ============================================================
  
  const emit = defineEmits([
    "update:modelValue",
    "close",
    "open-paywall"
  ]);
  
  // ============================================================
  // ACTIONS
  // ============================================================
  
  const closeDrawer = () => {
    emit("update:modelValue", false);
    emit("close");
  };
  
  const openPaywall = () => {
    emit("update:modelValue", false);
    emit("open-paywall");
  };
  
  // ============================================================
  // ESC KEY
  // ============================================================
  
  const handleKeyDown = (event) => {
    if (event.key === "Escape" && props.modelValue) {
      closeDrawer();
    }
  };
  
  watch(
    () => props.modelValue,
    (isOpen) => {
      if (isOpen) {
        window.addEventListener("keydown", handleKeyDown);
      } else {
        window.removeEventListener("keydown", handleKeyDown);
      }
    },
    { immediate: true }
  );
  
  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
  </script>
  
  <style scoped>
  
  /* ============================================================
     OVERLAY
     ============================================================ */
  
  .renew-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
  
    display: flex;
    justify-content: center;
    align-items: flex-end;
  
    padding: 20px;
    padding-bottom: max(
      20px,
      env(safe-area-inset-bottom)
    );
  
    box-sizing: border-box;
  
    background: rgba(10, 16, 27, 0.46);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
  }
  
  /* ============================================================
     DRAWER
     ============================================================ */
  
  .renew-drawer {
    box-sizing: border-box;
  
    width: 100%;
    max-width: 420px;
  
    padding: 21px;
  
    background: #ffffff;
  
    border: 1px solid rgba(30, 48, 80, 0.06);
    border-radius: 22px;
  
    box-shadow:
      0 22px 65px rgba(12, 25, 48, 0.20),
      0 4px 15px rgba(12, 25, 48, 0.05);
  
    font-family: "Eastman-Regular", sans-serif;
  }
  
  /* ============================================================
     HEADER
     ============================================================ */
  
  .renew-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }
  
  .renew-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  
    width: 43px;
    height: 43px;
  
    flex-shrink: 0;
  
    color: #2173e5;
    background: #eaf2ff;
  
    border-radius: 13px;
  }
  
  .renew-heading {
    flex: 1;
    min-width: 0;
  
    padding-top: 2px;
  }
  
  .renew-eyebrow {
    display: block;
  
    margin-bottom: 6px;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 9px;
  
    letter-spacing: 1px;
    color: #2173e5;
  }
  
  .renew-heading h3 {
    margin: 0;
  
    font-family: "Eastman-Bold", sans-serif;
    font-size: 15px;
    line-height: 1.4;
    letter-spacing: -0.25px;
  
    color: #202938;
  }
  
  .renew-close {
    display: flex;
    align-items: center;
    justify-content: center;
  
    width: 27px;
    height: 27px;
  
    flex-shrink: 0;
  
    padding: 0;
    border: none;
    border-radius: 8px;
  
    background: transparent;
  
    font-family: Arial, sans-serif;
    font-size: 24px;
    line-height: 1;
  
    color: #929cad;
    cursor: pointer;
  
    transition:
      background 180ms ease,
      color 180ms ease;
  }
  
  .renew-close:hover {
    color: #29374d;
    background: #f0f2f6;
  }
  
  /* ============================================================
     DESCRIPTION
     ============================================================ */
  
  .renew-description {
    margin: 15px 0 19px;
  
    font-family: "Eastman-Regular", sans-serif;
    font-size: 12px;
    line-height: 1.65;
  
    color: #717c8e;
  }
  
  /* ============================================================
     ACTIONS
     ============================================================ */
  
  .renew-actions {
    display: grid;
  
    grid-template-columns: 0.85fr 1.65fr;
    gap: 9px;
  }
  
  .renew-later,
  .renew-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  
    min-height: 42px;
  
    padding: 10px 12px;
  
    border: none;
    border-radius: 11px;
  
    font-family: "Eastman-Medium", sans-serif;
    font-size: 11px;
  
    cursor: pointer;
  
    transition:
      background-color 180ms ease,
      transform 180ms ease,
      box-shadow 180ms ease;
  }
  
  .renew-later {
    background: #f0f3f8;
    color: #5e6b7e;
  }
  
  .renew-later:hover {
    background: #e7ebf2;
  }
  
  .renew-primary {
    background: #2173e5;
    color: #ffffff;
  
    box-shadow:
      0 6px 16px rgba(33, 115, 229, 0.20);
  }
  
  .renew-primary:hover {
    background: #1768d7;
    box-shadow:
      0 8px 20px rgba(33, 115, 229, 0.27);
  }
  
  .renew-primary svg {
    flex-shrink: 0;
  }
  
  .renew-later:active,
  .renew-primary:active {
    transform: scale(0.98);
  }
  
  /* ============================================================
     FOOTNOTE
     ============================================================ */
  
  .renew-footnote {
    margin: 13px 0 0;
  
    text-align: center;
  
    font-family: "Eastman-Regular", sans-serif;
    font-size: 9px;
    line-height: 1.4;
  
    color: #9ba5b4;
  }
  
  /* ============================================================
     ANIMATION
     ============================================================ */
  
  .renew-fade-enter-active,
  .renew-fade-leave-active {
    transition: opacity 220ms ease;
  }
  
  .renew-fade-enter-from,
  .renew-fade-leave-to {
    opacity: 0;
  }
  
  .renew-slide-enter-active,
  .renew-slide-leave-active {
    transition:
      transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1),
      opacity 280ms ease;
  }
  
  .renew-slide-enter-from,
  .renew-slide-leave-to {
    transform: translateY(35px) scale(0.97);
    opacity: 0;
  }
  
  /* ============================================================
     DARK THEME
     ============================================================ */
  
  :global(html[data-theme="dark"]) .renew-overlay {
    background: rgba(0, 0, 0, 0.70);
  }
  
  :global(html[data-theme="dark"]) .renew-drawer {
    background: #1d1e22;
  
    border-color: rgba(255, 255, 255, 0.07);
  
    box-shadow:
      0 24px 70px rgba(0, 0, 0, 0.38);
  }
  
  :global(html[data-theme="dark"]) .renew-icon {
    background: rgba(40, 135, 247, 0.13);
    color: #73b0ff;
  }
  
  :global(html[data-theme="dark"]) .renew-eyebrow {
    color: #73b0ff;
  }
  
  :global(html[data-theme="dark"]) .renew-heading h3 {
    color: #f3f4f7;
  }
  
  :global(html[data-theme="dark"]) .renew-description {
    color: #a3a8b2;
  }
  
  :global(html[data-theme="dark"]) .renew-close {
    color: #9298a4;
  }
  
  :global(html[data-theme="dark"]) .renew-close:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #ffffff;
  }
  
  :global(html[data-theme="dark"]) .renew-later {
    background: #303238;
    color: #d2d5dc;
  }
  
  :global(html[data-theme="dark"]) .renew-later:hover {
    background: #3b3d44;
  }
  
  :global(html[data-theme="dark"]) .renew-footnote {
    color: #777e8a;
  }
  
  /* ============================================================
     MOBILE
     ============================================================ */
  
  @media (max-width: 480px) {
    .renew-overlay {
      padding: 12px;
      padding-bottom: max(
        12px,
        env(safe-area-inset-bottom)
      );
    }
  
    .renew-drawer {
      padding: 19px;
      border-radius: 19px;
    }
  
    .renew-heading h3 {
      font-size: 14px;
    }
  
    .renew-description {
      font-size: 11.5px;
    }
  
    .renew-actions {
      grid-template-columns: 0.8fr 1.7fr;
      gap: 8px;
    }
  
    .renew-primary,
    .renew-later {
      font-size: 10.5px;
    }
  }
  
  /* ============================================================
     REDUCED MOTION
     ============================================================ */
  
  @media (prefers-reduced-motion: reduce) {
    .renew-fade-enter-active,
    .renew-fade-leave-active,
    .renew-slide-enter-active,
    .renew-slide-leave-active {
      transition: none;
    }
  }
  
  </style>
  